import React from 'react';
import { motion } from 'framer-motion';
import { useInView } from 'react-intersection-observer';

const features = [
  'Ground floor is completely screen-free',
  'Focus on traditional, proven learning methods',
  'Sensory-based educational activities',
  'Individual attention for each child',
  'Safe, nurturing environment',
];

const highlights = [
  {
    title: 'Screen-Free Environment',
    description:
      'Our ground floor classrooms are completely free from screens, allowing children to engage with the physical world around them.',
    color: 'brand-yellow',
  },
  {
    title: 'Research-Based Approach',
    description:
      'Following AAP guidelines that recommend limiting screen time for healthy development in young children.',
    color: 'brand-blue',
  },
];

const About: React.FC = () => {
  const [ref, inView] = useInView({
    triggerOnce: true,
    threshold: 0.1,
  });

  return (
    <section id="about" className="bg-brand-light p-16">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <motion.div
          ref={ref}
          initial={{ opacity: 0, y: 50 }}
          animate={inView ? { opacity: 1, y: 0 } : { opacity: 0, y: 50 }}
          transition={{ duration: 0.8, ease: 'easeOut' }}
          className="text-center mb-16"
        >
          <h2 className="text-4xl font-bold text-brand-pink mb-8">About Neev Play School</h2>
          <p className="text-lg text-brand-purple max-w-2xl mx-auto">
            Nurturing young minds through natural, hands-on learning
          </p>
        </motion.div>
        <div className="max-w-4xl mx-auto">
          {/* Center: Philosophy */}
          <motion.div
            initial={{ opacity: 0, y: 50 }}
            animate={inView ? { opacity: 1, y: 0 } : { opacity: 0, y: 50 }}
            transition={{ duration: 0.8, delay: 0.2, ease: 'easeOut' }}
            className="text-center mb-16"
          >
            <h3 className="text-2xl font-bold text-brand-blue mb-8">Our Philosophy</h3>
            <p className="text-base text-brand-dark mb-8 leading-relaxed max-w-3xl mx-auto text-justify ">
              At Neev Play School, we believe in nurturing young minds through hands-on, sensory-based learning. Our approach is grounded in child development research and focuses on creating meaningful experiences that promote healthy growth.
            </p>
            <ul className="space-y-3 text-brand-dark max-w-[300px] mx-auto">
              {features.map((feature, idx) => (
                <motion.li
                  key={idx}
                  initial={{ opacity: 0, y: 20 }}
                  animate={inView ? { opacity: 1, y: 0 } : { opacity: 0, y: 20 }}
                  transition={{ duration: 0.5, delay: 0.4 + idx * 0.1, ease: 'easeOut' }}
                  className="flex items-start text-base "
                >
                  <span className="text-brand-pink text-xl mr-2 select-none">✓</span>
                  <span className="block text-justify ">{feature}</span>
                </motion.li>
              ))}
            </ul>
          </motion.div>
          
          {/* Below: Highlights */}
          <motion.div
            initial={{ opacity: 0, y: 50 }}
            animate={inView ? { opacity: 1, y: 0 } : { opacity: 0, y: 50 }}
            transition={{ duration: 0.8, delay: 0.4, ease: 'easeOut' }}
            className="grid md:grid-cols-2 gap-8 max-w-4xl mx-auto"
          >
            {highlights.map((highlight, idx) => (
              <motion.div
                key={idx}
                initial={{ opacity: 0, y: 30 }}
                animate={inView ? { opacity: 1, y: 0 } : { opacity: 0, y: 30 }}
                transition={{ duration: 0.6, delay: 0.6 + idx * 0.2, ease: 'easeOut' }}
                className={`bg-${highlight.color} bg-opacity-10 border-l-8 border-${highlight.color} rounded-2xl py-6 px-16 shadow-bubble w-full`}
              >
                <h4 className={`text-lg font-bold text-${highlight.color} mb-2`}>
                  {highlight.title}
                </h4>
                <p className="text-base text-brand-dark text-justify ">
                  {highlight.description}
                </p>
              </motion.div>
            ))}
          </motion.div>
        </div>
      </div>
    </section>
  );
};

export default About; 